import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { Plus, X, Trash, PencilSimple, Factory, MapPin, Upload, ArrowsLeftRight } from "@phosphor-icons/react";
import ReassignDialog from "@/components/ReassignDialog";
import ImportModal from "@/components/ImportModal";
import { scopedCompanies as scopeCompanies, defaultCompanyId } from "@/lib/companyScope";

export default function Installations() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reassignId, setReassignId] = useState(null);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  const reload = () => api.get("/installations").then(r=>setItems(r.data));
  useEffect(()=>{
    reload();
    api.get("/catalogs", { params: { type: "tipo_instalacion" } }).then(r=>setTypes(r.data));
    api.get("/companies").then(r=>setCompanies(r.data));
  },[user]);

  const onDelete = async (id) => {
    if(!window.confirm("¿Eliminar instalación?")) return;
    try{ await api.delete(`/installations/${id}`); toast.success("Eliminada"); reload(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Error"); if (e?.response?.status === 409 && isAdmin) setReassignId(id); }
  };

  const canEdit = can("installations", "write");
  const canCreate = can("installations", "write");
  const canDelete = can("installations", "delete");

  return (
    <div className="space-y-6" data-testid="installations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Operación</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Instalaciones</h1>
          <p className="text-neutral-500 mt-2">Plantas, bodegas y centros operativos de cada empresa.</p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap gap-2">
            <button onClick={()=>setImportOpen(true)} className="inline-flex items-center gap-2 px-4 py-3 bg-white border border-neutral-300 hover:border-neutral-400 text-neutral-800 font-semibold rounded-md" data-testid="import-installations-btn">
              <Upload size={18} weight="bold"/> Importar
            </button>
            <button onClick={()=>{setEditing(null); setOpen(true);}} className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md" data-testid="new-installation-btn">
              <Plus size={18} weight="bold"/> Nueva instalación
            </button>
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(it=>(
          <div key={it.id} onClick={()=>nav(`/instalaciones/${it.id}`)} className="bg-white border border-neutral-200 rounded-md p-5 hover:-translate-y-0.5 hover:border-blue-400 transition-all cursor-pointer" data-testid={`installation-${it.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 rounded-md bg-gradient-to-br from-indigo-600 to-blue-700 text-white flex items-center justify-center">
                <Factory size={20} weight="duotone"/>
              </div>
              {(canEdit || canDelete || isAdmin) && (
                <div className="flex gap-1" onClick={(e)=>e.stopPropagation()}>
                  {isAdmin && <button onClick={()=>setReassignId(it.id)} title="Reasignar obligaciones y documentos" className="p-1.5 text-neutral-400 hover:text-blue-600" data-testid={`reassign-installation-${it.id}`}><ArrowsLeftRight size={16}/></button>}
                  {canEdit && <button onClick={()=>{setEditing(it); setOpen(true);}} className="p-1.5 text-neutral-400 hover:text-blue-600" data-testid={`edit-installation-${it.id}`}><PencilSimple size={16}/></button>}
                  {canDelete && <button onClick={()=>onDelete(it.id)} className="p-1.5 text-neutral-400 hover:text-rose-500" data-testid={`delete-installation-${it.id}`}><Trash size={16}/></button>}
                </div>
              )}
            </div>
            <h3 className="font-display text-lg font-bold tracking-tight">{it.name}</h3>
            {it.type && <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">{it.type}</span>}
            {it.code && <p className="text-xs text-neutral-500 mt-2">Permiso: {it.code}</p>}
            {it.address && <p className="text-xs text-neutral-600 mt-2 flex items-start gap-1"><MapPin size={12} className="mt-0.5 flex-shrink-0"/> {it.address}</p>}
            {it.responsible && <p className="text-xs text-neutral-500 mt-1">Responsable: {it.responsible}</p>}
          </div>
        ))}
        {items.length===0 && <div className="md:col-span-3 py-16 text-center text-neutral-400 border border-dashed border-neutral-300 rounded-md"><Factory size={42} className="mx-auto mb-3 text-neutral-300" weight="duotone"/><p>Sin instalaciones aún.</p></div>}
      </div>
      {reassignId && <ReassignDialog kind="installation" sourceId={reassignId} onClose={()=>setReassignId(null)} onDone={()=>{setReassignId(null); reload();}}/>}
      {open && <Form user={user} companies={companies} types={types} initial={editing} onClose={()=>setOpen(false)} onSuccess={()=>{setOpen(false); reload();}}/>}
      {importOpen && (
        <ImportModal
          title="Importar instalaciones"
          templateUrl="/installations/import/template"
          templateFilename="plantilla_instalaciones.csv"
          previewUrl="/installations/import/preview"
          importUrl="/installations/import"
          columnsHint={user?.role === "super_admin"
            ? "name, company_name (o company_tax_id), type, code, address, responsible, description"
            : "name, type, code, address, responsible, description  (se asigna a la empresa activa)"}
          columns={[
            { key: "name", label: "Nombre" },
            { key: "company_name", label: "Empresa" },
            { key: "code", label: "Permiso" },
            { key: "type", label: "Tipo" },
          ]}
          onClose={()=>setImportOpen(false)}
          onSuccess={()=>{ setImportOpen(false); reload(); }}
          testidPrefix="import-installations"
        />
      )}
    </div>
  );
}

function Form({user, companies, types, initial, onClose, onSuccess}){
  const defaultType = types[0]?.name || "";
  const [f, setF] = useState(initial || {name:"", company_id: defaultCompanyId(user), address:"", type:defaultType, code:"", description:"", responsible:""});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Filtrar empresas del selector por el scope activo (si lo hay)
  const scopedCompanies = useMemo(() => scopeCompanies(user, companies), [companies, user]);
  const save = async () => {
    if(!f.name) return toast.error("Nombre requerido");
    if(!f.company_id) return toast.error("Empresa requerida");
    setBusy(true);
    try{
      if(initial) await api.put(`/installations/${initial.id}`,f);
      else await api.post("/installations",f);
      toast.success("Guardado"); onSuccess();
    }catch(e){ toast.error(e?.response?.data?.detail||"Error"); }finally{setBusy(false);}
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-xl border border-neutral-200 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-xl font-bold">{initial?"Editar":"Nueva"} instalación</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-3">
          {(user.role==="super_admin" || scopedCompanies.length > 1) && (
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Empresa</label>
              <select value={f.company_id} onChange={e=>setF({...f,company_id:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="inst-form-company">
                <option value="">Seleccionar...</option>
                {scopedCompanies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {scopedCompanies.length < companies.length && (
                <p className="text-xs text-neutral-500 mt-1">Mostrando solo empresas del contexto activo.</p>
              )}
            </div>
          )}
          {[["name","Nombre"],["code","Permiso"],["responsible","Responsable"]].map(([k,l])=>(
            <div key={k}><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">{l}</label>
              <input value={f[k]||""} onChange={e=>setF({...f,[k]:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid={`inst-form-${k}`}/></div>
          ))}
          <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Tipo</label>
            <select value={f.type||""} onChange={e=>setF({...f,type:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="inst-form-type">
              <option value="">Seleccionar...</option>
              {types.map(t=><option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
            {types.length===0 && <p className="text-xs text-amber-600 mt-1">Sin tipos. El Super Admin debe agregarlos en Catálogos.</p>}
          </div>
          <div className="md:col-span-2"><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Dirección</label>
            <input value={f.address||""} onChange={e=>setF({...f,address:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="inst-form-address"/></div>
          <div className="md:col-span-2"><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={f.description||""} onChange={e=>setF({...f,description:e.target.value})} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md"/></div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="inst-form-submit">{busy?"Guardando...":"Guardar"}</button>
        </div>
      </div>
    </div>
  );
}
